<script setup lang="ts">
    import { ref, onMounted, computed, watch } from 'vue';
    import { useRouter } from 'vue-router';
    import axios from 'axios';
    import { useNotificationStore } from '@/stores/notificationStore';
    import chatService, { type ChatConversation } from '@/services/chatService';
    import { cleanupSocketListeners } from '@/services/socketListenerService';
    import { formatDate } from '@/utils';

    // eslint-disable-next-line no-undef
    const props = defineProps(['appSidebarOpened'])
    const router = useRouter();
    const notificationStore = useNotificationStore();

    const appNavigationOpened = ref(false);
    const conversationsWithUnread = ref<ChatConversation[]>([]);
    const loadingNotifications = ref(false);

    // Compteur total de messages non lus
    const unreadCount = computed(() => notificationStore.unreadMessagesCount);

    // Surveiller les changements du store pour rafraîchir la liste si le widget est ouvert
    watch(() => notificationStore.unreadMessagesCount, () => {
        if (appNavigationOpened.value) {
            loadNotifications();
        }
    });

    function toggleAppNavigation(isOpen: boolean) {
        appNavigationOpened.value = isOpen;
        if (isOpen) {
            loadNotifications();
        }
    }

    // Charger les conversations avec messages non lus
    async function loadNotifications() {
        try {
            loadingNotifications.value = true;
            const allConversations = await chatService.getConversations();
            // Filtrer seulement celles avec des messages non lus
            conversationsWithUnread.value = allConversations.filter(conv => {
                const unread = notificationStore.getUnreadForConversation(conv.id);
                return unread > 0 || (conv.unreadCount && conv.unreadCount > 0);
            });
        } catch (error) {
            console.error('Erreur lors du chargement des notifications:', error);
        } finally {
            loadingNotifications.value = false;
        }
    }

    // Aller à une conversation
    function goToConversation(conversationId: number) {
        toggleAppNavigation(false);
        router.push(`/app/service-client/${conversationId}`);
    }

    function doLogout() {
        // Nettoyer les listeners Socket.IO avant la déconnexion
        cleanupSocketListeners();

        const accessToken = localStorage.getItem('LS_COOP_FINANCE_BO_ACCESS_TOKEN');
        axios.post('/backoffice/auth/signout', {}, { headers: { Authorization: `Bearer ${accessToken}` } });
        localStorage.removeItem('LS_COOP_FINANCE_BO_ACCESS_TOKEN');
        localStorage.removeItem('LS_COOP_FINANCE_BO_AUTH_USER');
        router.replace('/auth/login');
    }

    // Obtenir le type de conversation en français
    function getTypeLabel(type: string): string {
        const types: Record<string, string> = {
            'general': 'Général',
            'reclamation': 'Réclamation',
            'remboursement': 'Remboursement',
            'compte': 'Problème compte',
            'carte': 'Carte',
            'autre': 'Autre',
        };
        return types[type] || type;
    }

    // Obtenir le nombre de messages non lus pour une conversation
    function getUnreadForConversation(conversationId: number): number {
        return notificationStore.getUnreadForConversation(conversationId);
    }

    onMounted(() => {
        // Charger les notifications initiales
        loadNotifications();

        // Note: Les listeners Socket.IO sont maintenant gérés globalement
        // via socketListenerService.ts, initialisé dans le router
        // On utilise un watcher sur le store pour rafraîchir la liste si besoin

        // Rafraîchir périodiquement (toutes les 30 secondes)
        setInterval(() => {
            if (appNavigationOpened.value) {
                loadNotifications();
            }
        }, 30000);
    });
</script>

<template>
    <!--Nav-->
    <header class="app-nav navigation" :class="{'close': !appSidebarOpened}"><!-- close -->
        <div class="navigation-wrapper">

            <div class="navigation-split-left">
                <div class="s10_mobile flex align-items-center px-5 py-5 gap-4 rounded-50 backdrop-filter">
                    <img src="/icons/icon-menu-securite.svg" class="size-lg" />
                    <h2 class="font-semibold text-black">Sécurité </h2>
                    <div class="my-badge dot bg-color-notif position-relative mt--5"></div>
                </div>
            </div>

            <div class="navigation-split-right">
                <nav class="navigation-main nav">
                    <div class="navigation-search-set">
                        <div class="navigation-search"><!-- autocomplete -->
                            <input type="text" name="search" id="search" class="navigation-search-field" placeholder="Rechercher   #module" />
                            <div class="navigation-search-effect"></div>
                        </div>
                        <div class="search-result"><!-- autocomplete -->
                            <li><img src="/icons/icon-hashtag.svg" /> Module Sécurité</li>
                            <li><img src="/icons/icon-hashtag.svg" /> Carte Coop Finance</li>
                            <li><img src="/icons/icon-hashtag.svg" /> Article market place</li>
                            <li><img src="/icons/icon-hashtag.svg" /> Module Sécurité</li>
                            <li><img src="/icons/icon-hashtag.svg" /> Carte Coop Finance</li>
                            <li><img src="/icons/icon-hashtag.svg" /> Article market place</li>
                        </div>
                    </div>
                    <ul>
                        <li class="btn-action tips bottom active mx-3 notification-open" @click="toggleAppNavigation(true)">
                            <img src="/icons/icon-notification.svg" class="btn-action-icon black" alt="" />
                            <!-- <span>Notification</span> -->
                            <!-- Badge dynamique avec le nombre de messages non lus -->
                            <template v-if="unreadCount > 0">
                                <div class="my-badge tl bg-color-danger notification-badge">
                                    {{ unreadCount > 99 ? '99+' : unreadCount }}
                                </div>
                            </template>
                            <template v-else>
                                <div class="my-badge tl dot bg-color-danger"></div>
                            </template>
                        </li>
                        <li>
                            <!-- <span class="text-base font-medium">Juliette Aké</span> <img src="/icons/icon-arrow-down.svg" class="ml-2 size-xs opacity-5" alt="" /> -->
                            <img src="/assets/users/600w/user11.jpg" class="user-profil-img rounded-50 mt-1">
                            <ul class="sub-menu">
                                <li>
                                    <router-link to="/app/account">
                                        <img src="/icons/icon-menu-securite.svg" class="size-xs icon-white mr-2" /> 
                                        Mon compte
                                    </router-link>
                                </li>
                                <!-- <li><a>Mes paramètres</a></li> -->
                                <li><a @click="doLogout()"><img src="/icons/icon-deconnexion.svg" class="size-xs icon-white mr-2" /> Déconnexion</a></li>
                            </ul>
                            
                        </li>
                    </ul>
                </nav>
            </div>
        </div>
    </header>
    <!--End Header-->

    <div class="widget-notification" :class="{'open': appNavigationOpened}">
        <button class="btn white white2 px-3 py-3 align-right opacity-7 notification-close" @click="toggleAppNavigation(false)">
            <img src="/icons/icon-close.svg" class="size-sm icon-black" />
        </button>
        <div class=" flex align-items-center py-5 gap-4 clear-both">
            <img src="/icons/icon-notification.svg" class="size-lg" />
            <h2 class="font-semibold text-black">Notifications</h2>
            <!-- Badge avec le nombre total de messages non lus -->
            <template v-if="unreadCount > 0">
                <div class="my-badge bg-color-danger notification-badge position-relative mt--5">
                    {{ unreadCount > 99 ? '99+' : unreadCount }}
                </div>
            </template>
            <template v-else>
                <div class="my-badge dot bg-color-danger position-relative mt--5"></div>
            </template>
        </div>
        <div class="h-15"></div>
        
        <!-- Loading -->
        <template v-if="loadingNotifications">
            <div class="text-center py-5">
                <p class="text-grey-500">Chargement des notifications...</p>
            </div>
        </template>
        
        <!-- Liste des notifications -->
        <template v-else-if="conversationsWithUnread.length === 0">
            <div class="text-center py-5">
                <p class="text-grey-500">Aucune notification</p>
            </div>
        </template>
        
        <ul v-else>
            <li v-for="conversation in conversationsWithUnread" :key="conversation.id">
                <a href="#" @click.prevent="goToConversation(conversation.id)">
                    <div class="flex align-items-center mb-2">
                        <img 
                            v-if="conversation.user?.avatar" 
                            :src="conversation.user.avatar" 
                            class="size-md rounded-100 mr-3" 
                            alt=""
                        />
                        <div v-else class="size-md rounded-100 mr-3 bg-grey-200 flex align-items-center justify-content-center">
                            <span class="text-xs">{{ conversation.user?.prenom?.[0] || 'U' }}</span>
                        </div>
                        <div class="flex-1">
                            <h6 class="font-normal text-black my-1">
                                {{ conversation.user?.prenom }} {{ conversation.user?.nom }}
                            </h6>
                            <p class="text-sm text-grey-500 mb-0">
                                {{ getTypeLabel(conversation.type) }}
                            </p>
                        </div>
                        <div class="my-badge br2 bg-color-danger notification-badge">
                            {{ getUnreadForConversation(conversation.id) || conversation.unreadCount || 0 }}
                        </div>
                    </div>
                    <p v-if="conversation.dernierMessage" class="text-sm text-grey-500 mt-2">
                        {{ conversation.dernierMessage.texte.substring(0, 60) }}{{ conversation.dernierMessage.texte.length > 60 ? '...' : '' }}
                    </p>
                    <p class="text-xs text-grey-400 mt-1">
                        {{ formatDate(conversation.dernierMessage?.date || conversation.updated_at) }}
                    </p>
                </a>
            </li>
        </ul>
    </div>
</template>
